// Helpers for the stable IABF person layer (`iabf-people`).
// Only touches iabf-* collections — shared Broche data is out of bounds.

import {
  IABF_PEOPLE_COLLECTION,
  IABF_PERSON_PROFILE_FIELDS,
  IABF_DANCER_YEAR,
  dancersCollection,
  col
} from '@/config/iabf-event'

const ENROLLMENT_YEARS = [2024, 2025, 2026, 2027]
const CANCELED_COLLECTIONS = [
  col('canceled-dancers', 2026),
  'iabf-canceled-dancers',
  col('canceled-dancers', 2027)
]

/** Tombstone left behind after two person records are linked. */
export function isMergedPersonRecord (d) {
  if (!d) {
    return false
  }
  if (d.mergedInto) {
    return true
  }
  const email = String(d.email || '').toLowerCase()
  return email.indexOf('merged+') === 0 || email.indexOf('@iabf.invalid') !== -1
}

export function uniqueEmails (list) {
  const seen = new Set()
  const out = []
  for (const raw of list || []) {
    const e = normalizeEmail(raw)
    if (!e || seen.has(e)) {
      continue
    }
    seen.add(e)
    out.push(e)
  }
  return out
}

export function normalizeEmail (email) {
  return String(email || '').trim().toLowerCase()
}

export function pickProfileFields (source) {
  const out = {}
  if (!source) {
    return out
  }
  for (const key of IABF_PERSON_PROFILE_FIELDS) {
    if (source[key] !== undefined && source[key] !== null) {
      out[key] = source[key]
    }
  }
  return out
}

/** Prefer non-empty overlay values; keep base otherwise. */
export function mergeProfilePreferNonEmpty (base, overlay) {
  const out = { ...(base || {}) }
  for (const key of IABF_PERSON_PROFILE_FIELDS) {
    const next = overlay && overlay[key]
    if (next === undefined || next === null) {
      continue
    }
    if (typeof next === 'string' && next.trim() === '') {
      continue
    }
    if (next === false && out[key]) {
      // Don't clobber a true "entered" flag with false from an older year.
      if (key.endsWith('Entered')) {
        continue
      }
    }
    out[key] = next
  }
  return out
}

export async function findPersonByEmail (db, email) {
  const lower = normalizeEmail(email)
  if (!lower) {
    return null
  }
  let snapshot = await db.collection(IABF_PEOPLE_COLLECTION).where('email', '==', lower).limit(1).get()
  if (!snapshot.empty) {
    const doc = snapshot.docs[0]
    const rec = { id: doc.id, ...doc.data() }
    if (!isMergedPersonRecord(rec)) {
      return rec
    }
  }
  snapshot = await db.collection(IABF_PEOPLE_COLLECTION)
    .where('previousEmails', 'array-contains', lower)
    .limit(1)
    .get()
  if (!snapshot.empty) {
    const doc = snapshot.docs[0]
    const rec = { id: doc.id, ...doc.data() }
    if (!isMergedPersonRecord(rec)) {
      return rec
    }
  }
  // Try original casing (legacy) — do not full-scan; non-admins only own their docs.
  if (email && email !== lower) {
    snapshot = await db.collection(IABF_PEOPLE_COLLECTION).where('email', '==', email).limit(1).get()
    if (!snapshot.empty) {
      const doc = snapshot.docs[0]
      return { id: doc.id, ...doc.data() }
    }
  }
  return null
}

export async function findPersonByUid (db, uid) {
  if (!uid) {
    return null
  }
  const snapshot = await db.collection(IABF_PEOPLE_COLLECTION).where('uid', '==', uid).limit(1).get()
  if (snapshot.empty) {
    return null
  }
  const doc = snapshot.docs[0]
  return { id: doc.id, ...doc.data() }
}

export async function loadPersonById (db, personId) {
  if (!personId) {
    return null
  }
  const doc = await db.collection(IABF_PEOPLE_COLLECTION).doc(personId).get()
  if (!doc.exists) {
    return null
  }
  return { id: doc.id, ...doc.data() }
}

/**
 * Find or create a person for an enrollment-shaped payload.
 * Admin / migration use. Returns person id.
 */
/**
 * Remove a festival year from a person's yearsAttended (e.g. after hard-deleting
 * that year's enrollment). No-op if the year isn't present.
 * @returns {Promise<number[]|null>} updated years array, or null if person missing
 */
export async function removeYearFromPerson (db, personId, year) {
  const id = String(personId || '').trim()
  const y = Number(year)
  if (!id || !Number.isFinite(y) || y <= 0) {
    return null
  }
  const ref = db.collection(IABF_PEOPLE_COLLECTION).doc(id)
  const snap = await ref.get()
  if (!snap.exists) {
    return null
  }
  const data = snap.data() || {}
  const prev = Array.isArray(data.yearsAttended) ? data.yearsAttended : []
  const hadYear = prev.some(n => Number(n) === y)
  const next = prev
    .map(n => Number(n))
    .filter(n => Number.isFinite(n) && n > 0 && n !== y)
    .sort((a, b) => a - b)
  if (!hadYear) {
    return next
  }
  await ref.set({
    yearsAttended: next,
    updatedAt: Date.now()
  }, { merge: true })
  return next
}

export async function findOrCreatePerson (db, enrollmentLike, { yearsAttended = [], festivalYear } = {}) {
  const email = normalizeEmail(enrollmentLike.email)
  if (!email) {
    throw new Error('Cannot create person without email')
  }

  const existing = await findPersonByEmail(db, email)
  const profile = pickProfileFields({
    ...enrollmentLike,
    email
  })
  const now = Date.now()

  if (existing) {
    const updates = mergeProfilePreferNonEmpty(existing, profile)
    const yearSet = new Set([...(existing.yearsAttended || []), ...yearsAttended])
    const payload = {
      ...updates,
      email,
      yearsAttended: Array.from(yearSet).sort((a, b) => a - b),
      updatedAt: now
    }
    if (enrollmentLike.uid && !existing.uid) {
      payload.uid = enrollmentLike.uid
    }
    if (enrollmentLike.stripeCustomerId && !existing.stripeCustomerId) {
      payload.stripeCustomerId = enrollmentLike.stripeCustomerId
    }
    await db.collection(IABF_PEOPLE_COLLECTION).doc(existing.id).set(payload, { merge: true })
    return existing.id
  }

  const ref = await db.collection(IABF_PEOPLE_COLLECTION).add({
    ...profile,
    email,
    uid: enrollmentLike.uid || null,
    stripeCustomerId: enrollmentLike.stripeCustomerId || null,
    yearsAttended: Array.from(new Set(yearsAttended)).sort((a, b) => a - b),
    createdAt: now,
    updatedAt: now,
    createdFromYear: festivalYear || IABF_DANCER_YEAR
  })
  return ref.id
}

/**
 * Resolve person for a loaded enrollment: by personId, then email, then uid.
 */
export async function resolvePersonForEnrollment (db, enrollment) {
  if (!enrollment) {
    return null
  }
  try {
    if (enrollment.personId) {
      const byId = await loadPersonById(db, enrollment.personId)
      if (byId) {
        return byId
      }
    }
    if (enrollment.email) {
      const byEmail = await findPersonByEmail(db, enrollment.email)
      if (byEmail) {
        return byEmail
      }
    }
    if (enrollment.uid) {
      return await findPersonByUid(db, enrollment.uid)
    }
  } catch (e) {
    // Rules / missing person must not break login or dashboard hydrate
    console.warn('resolvePersonForEnrollment failed:', e)
  }
  return null
}

/**
 * Merge person profile onto an enrollment object for UI.
 * Enrollment id always wins (ops point at the year doc).
 */
export function enrollmentWithPersonProfile (enrollment, person) {
  if (!enrollment) {
    return enrollment
  }
  if (!person) {
    return { ...enrollment }
  }
  return {
    ...enrollment,
    ...pickProfileFields(person),
    // Re-assert enrollment identity / linkage after profile overlay
    id: enrollment.id,
    personId: person.id || enrollment.personId,
    uid: enrollment.uid || person.uid || null,
    email: normalizeEmail(enrollment.email || person.email),
    yearsAttended: person.yearsAttended || []
  }
}

/** 1 → 1st, 2 → 2nd, 3 → 3rd, 11 → 11th */
export function ordinalYearLabel (n) {
  const v = Number(n)
  if (!Number.isFinite(v) || v < 1) {
    return ''
  }
  const mod100 = v % 100
  const mod10 = v % 10
  let suf = 'th'
  if (mod100 < 11 || mod100 > 13) {
    if (mod10 === 1) {
      suf = 'st'
    } else if (mod10 === 2) {
      suf = 'nd'
    } else if (mod10 === 3) {
      suf = 'rd'
    }
  }
  return String(v) + suf
}

export async function updatePersonProfile (db, personId, profilePatch) {
  if (!personId) {
    return
  }
  const patch = pickProfileFields(profilePatch)
  if (profilePatch.email) {
    patch.email = normalizeEmail(profilePatch.email)
  }
  await db.collection(IABF_PEOPLE_COLLECTION).doc(personId).set({
    ...patch,
    updatedAt: Date.now()
  }, { merge: true })
}

export async function linkUidToPerson (db, personId, uid) {
  if (!personId || !uid) {
    return
  }
  await db.collection(IABF_PEOPLE_COLLECTION).doc(personId).set({
    uid,
    updatedAt: Date.now()
  }, { merge: true })
}

export function currentDancersCollection () {
  return dancersCollection(IABF_DANCER_YEAR)
}

async function collectDocsByQueries (db, colName, queries) {
  const seen = new Set()
  const out = []
  const snaps = await Promise.all(queries.map((q) => q.get().catch(() => null)))
  snaps.forEach((snap) => {
    if (!snap || snap.empty) {
      return
    }
    snap.docs.forEach((doc) => {
      if (seen.has(doc.id)) {
        return
      }
      seen.add(doc.id)
      out.push(doc)
    })
  })
  return out
}

async function relinkFestivalRecords (db, {
  canonicalId,
  mergeFromId,
  emailsToMatch,
  newPrimaryEmail
}) {
  const emails = uniqueEmails(emailsToMatch)
  const primary = normalizeEmail(newPrimaryEmail)
  const now = Date.now()

  for (const year of ENROLLMENT_YEARS) {
    const colName = dancersCollection(year)
    if (!colName) {
      continue
    }
    const queries = [
      db.collection(colName).where('personId', '==', canonicalId)
    ]
    if (mergeFromId && mergeFromId !== canonicalId) {
      queries.push(db.collection(colName).where('personId', '==', mergeFromId))
    }
    emails.forEach((e) => {
      queries.push(db.collection(colName).where('email', '==', e))
    })
    const docs = await collectDocsByQueries(db, colName, queries)
    await Promise.all(docs.map((doc) => {
      const patch = { personId: canonicalId, updatedAt: now }
      if (primary) {
        patch.email = primary
      }
      return doc.ref.set(patch, { merge: true })
    }))
  }

  for (const colName of CANCELED_COLLECTIONS) {
    const queries = [
      db.collection(colName).where('personId', '==', canonicalId)
    ]
    if (mergeFromId && mergeFromId !== canonicalId) {
      queries.push(db.collection(colName).where('personId', '==', mergeFromId))
    }
    emails.forEach((e) => {
      queries.push(db.collection(colName).where('email', '==', e))
    })
    const docs = await collectDocsByQueries(db, colName, queries)
    await Promise.all(docs.map((doc) => {
      const patch = { personId: canonicalId, updatedAt: now }
      if (primary) {
        patch.email = primary
      }
      return doc.ref.set(patch, { merge: true })
    }))
  }

  const guestCollections = [
    col('guest-registrations', 2026),
    col('guest-registrations', 2027)
  ]
  for (const colName of guestCollections) {
    const queries = []
    emails.forEach((e) => {
      queries.push(db.collection(colName).where('dancerEmail', '==', e))
    })
    if (!queries.length) {
      continue
    }
    const docs = await collectDocsByQueries(db, colName, queries)
    await Promise.all(docs.map((doc) => {
      const patch = { updatedAt: now }
      if (primary) {
        patch.dancerEmail = primary
      }
      return doc.ref.set(patch, { merge: true })
    }))
  }
}

async function mergePersonInto (db, canonical, other) {
  if (!canonical || !other || canonical.id === other.id) {
    return canonical
  }
  const now = Date.now()
  const years = Array.from(new Set([
    ...(canonical.yearsAttended || []),
    ...(other.yearsAttended || [])
  ].map(Number).filter((n) => Number.isFinite(n) && n > 0))).sort((a, b) => a - b)
  const previousEmails = uniqueEmails([
    ...(canonical.previousEmails || []),
    ...(other.previousEmails || []),
    other.email,
    canonical.email
  ]).filter((e) => e !== normalizeEmail(canonical.email) && !isMergedPersonRecord({ email: e }))
  const payload = mergeProfilePreferNonEmpty(canonical, other)
  payload.yearsAttended = years
  payload.previousEmails = previousEmails
  payload.updatedAt = now
  if (!canonical.uid && other.uid) {
    payload.uid = other.uid
  }
  if (!canonical.stripeCustomerId && other.stripeCustomerId) {
    payload.stripeCustomerId = other.stripeCustomerId
  }
  await db.collection(IABF_PEOPLE_COLLECTION).doc(canonical.id).set(payload, { merge: true })
  // Keep the old doc so ids don't break, but take it off year rosters and
  // email lookup. The merged+…@iabf.invalid address is an internal tombstone.
  await db.collection(IABF_PEOPLE_COLLECTION).doc(other.id).set({
    mergedInto: canonical.id,
    email: 'merged+' + other.id + '@iabf.invalid',
    previousEmails: [],
    yearsAttended: [],
    yearsFaculty: [],
    updatedAt: now
  }, { merge: true })
  return { id: canonical.id, ...canonical, ...payload }
}

/** Fold a leftover merge stub's festival years onto the surviving person. */
export async function absorbMergedPersonStub (db, stub) {
  if (!stub || !stub.id || !stub.mergedInto) {
    return null
  }
  const canonical = await loadPersonById(db, stub.mergedInto)
  if (!canonical) {
    return null
  }
  const years = Array.from(new Set([
    ...(canonical.yearsAttended || []),
    ...(stub.yearsAttended || [])
  ].map(Number).filter((n) => Number.isFinite(n) && n > 0))).sort((a, b) => a - b)
  const now = Date.now()
  const canonicalYears = (canonical.yearsAttended || []).slice().sort((a, b) => a - b)
  const yearsChanged = years.join(',') !== canonicalYears.join(',')
  if (yearsChanged) {
    await db.collection(IABF_PEOPLE_COLLECTION).doc(canonical.id).set({
      yearsAttended: years,
      updatedAt: now
    }, { merge: true })
  }
  const stubStillOnRoster = (stub.yearsAttended || []).length > 0 ||
    !isMergedPersonRecord(stub)
  if (stubStillOnRoster) {
    await db.collection(IABF_PEOPLE_COLLECTION).doc(stub.id).set({
      yearsAttended: [],
      yearsFaculty: [],
      email: String(stub.email || '').indexOf('@iabf.invalid') !== -1
        ? stub.email
        : ('merged+' + stub.id + '@iabf.invalid'),
      previousEmails: [],
      mergedInto: stub.mergedInto,
      updatedAt: now
    }, { merge: true })
  }
  return { id: canonical.id, yearsAttended: years }
}

/**
 * Change a person's primary festival email. Keeps the old address as an alias
 * so history, deposits, and lookup still match. Updates year enrollments.
 */
export async function changePersonPrimaryEmail (db, { personId, newEmail, oldEmail }) {
  const next = normalizeEmail(newEmail)
  const prev = normalizeEmail(oldEmail)
  if (!next) {
    throw new Error('A valid email is required.')
  }
  let person = personId ? await loadPersonById(db, personId) : null
  if (!person && prev) {
    person = await findPersonByEmail(db, prev)
  }
  if (!person) {
    throw new Error('Could not find a person record for this dancer.')
  }
  if (next === normalizeEmail(person.email)) {
    return { personId: person.id, email: next, previousEmails: person.previousEmails || [] }
  }
  const other = await findPersonByEmail(db, next)
  if (other && other.id !== person.id) {
    person = await mergePersonInto(db, person, other)
  }
  const previousEmails = uniqueEmails([
    ...(person.previousEmails || []),
    person.email,
    prev
  ]).filter((e) => e !== next && !isMergedPersonRecord({ email: e }))
  await db.collection(IABF_PEOPLE_COLLECTION).doc(person.id).set({
    email: next,
    previousEmails,
    updatedAt: Date.now()
  }, { merge: true })
  await relinkFestivalRecords(db, {
    canonicalId: person.id,
    mergeFromId: other && other.id !== person.id ? other.id : null,
    emailsToMatch: [prev, person.email, next, ...(person.previousEmails || [])],
    newPrimaryEmail: next
  })
  return { personId: person.id, email: next, previousEmails }
}

/**
 * Attach another email's festival history to this person without changing
 * the primary email (unless this person has none).
 */
export async function linkPersonEmailAlias (db, { personId, aliasEmail }) {
  const alias = normalizeEmail(aliasEmail)
  if (!alias) {
    throw new Error('Enter the other email to link.')
  }
  let person = personId ? await loadPersonById(db, personId) : null
  if (!person) {
    throw new Error('Could not find a person record for this dancer.')
  }
  if (alias === normalizeEmail(person.email) || (person.previousEmails || []).indexOf(alias) !== -1) {
    return { personId: person.id, email: person.email, previousEmails: person.previousEmails || [] }
  }
  const other = await findPersonByEmail(db, alias)
  let mergeFromId = null
  if (other && other.id !== person.id) {
    mergeFromId = other.id
    person = await mergePersonInto(db, person, other)
  }
  const previousEmails = uniqueEmails([
    ...(person.previousEmails || []),
    alias,
    other && other.email
  ]).filter((e) => e !== normalizeEmail(person.email) && !isMergedPersonRecord({ email: e }))
  await db.collection(IABF_PEOPLE_COLLECTION).doc(person.id).set({
    previousEmails,
    updatedAt: Date.now()
  }, { merge: true })
  await relinkFestivalRecords(db, {
    canonicalId: person.id,
    mergeFromId,
    emailsToMatch: [alias, other && other.email, ...(other && other.previousEmails) || []],
    // Point year records at the canonical email so admin lists, login,
    // and owner rules keep matching after the link.
    newPrimaryEmail: person.email
  })
  return { personId: person.id, email: person.email, previousEmails }
}
